import { useEffect, useMemo, useRef, useState } from 'react';
import moment from 'moment';
import {
  Alert,
  Avatar,
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  Link,
  Paper,
  Slide,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material';
import ArchiveTwoToneIcon from '@mui/icons-material/ArchiveTwoTone';
import ArrowBackTwoToneIcon from '@mui/icons-material/ArrowBackTwoTone';
import AttachFileTwoToneIcon from '@mui/icons-material/AttachFileTwoTone';
import CloseTwoToneIcon from '@mui/icons-material/CloseTwoTone';
import EditTwoToneIcon from '@mui/icons-material/EditTwoTone';
import GroupsTwoToneIcon from '@mui/icons-material/GroupsTwoTone';
import OpenInFullTwoToneIcon from '@mui/icons-material/OpenInFullTwoTone';
import SendTwoToneIcon from '@mui/icons-material/SendTwoTone';

import { emitTeamTyping } from 'src/slices/teamChat';

const DRAWER_WIDTH = 376;

const initials = (name) => String(name || '?')
  .split(/\s+/)
  .filter(Boolean)
  .slice(0, 2)
  .map((part) => part[0])
  .join('')
  .toUpperCase();

function Attachment({ attachment, own }) {
  if (attachment.type === 'image') {
    return (
      <Box
        component="a"
        href={attachment.url}
        target="_blank"
        rel="noreferrer"
        sx={{ display: 'block', mt: 0.75 }}
      >
        <Box
          component="img"
          src={attachment.url}
          alt={attachment.filename || 'Team attachment'}
          sx={{ display: 'block', width: '100%', maxWidth: 240, maxHeight: 220, objectFit: 'cover', borderRadius: 1 }}
        />
      </Box>
    );
  }
  return (
    <Link
      href={attachment.url}
      target="_blank"
      rel="noreferrer"
      color={own ? 'inherit' : 'primary'}
      underline="hover"
      sx={{ display: 'block', mt: 0.75, overflowWrap: 'anywhere' }}
    >
      {attachment.filename || 'Open attachment'}
    </Link>
  );
}

function TeamConversation({
  open,
  embedded = false,
  drawerOpen,
  conversation,
  viewer,
  messages,
  loading,
  error,
  sending,
  sendError,
  hasMore,
  loadingMore,
  typingByActor,
  onClose,
  onOpenFull,
  onLoadMore,
  onRetry,
  onSend,
  onUpload,
  onManageGroup,
  onArchiveGroup
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [body, setBody] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const fileRef = useRef(null);
  const scrollRef = useRef(null);
  const typingTimer = useRef(null);
  const typingNames = useMemo(() => Object.values(typingByActor || {}), [typingByActor]);

  useEffect(() => {
    setBody('');
    setAttachments([]);
    setUploadError(null);
  }, [conversation?.id]);

  useEffect(() => {
    if (!scrollRef.current || loadingMore) return;
    scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages.length, loading, loadingMore]);

  useEffect(() => () => {
    clearTimeout(typingTimer.current);
    if (conversation?.id) emitTeamTyping(conversation.id, false);
  }, [conversation?.id]);

  const changeBody = (event) => {
    setBody(event.target.value);
    if (!conversation?.id) return;
    emitTeamTyping(conversation.id, true);
    clearTimeout(typingTimer.current);
    typingTimer.current = setTimeout(() => emitTeamTyping(conversation.id, false), 1400);
  };

  const submit = async () => {
    if ((!body.trim() && !attachments.length) || sending || uploading) return;
    const sent = await onSend({ body: body.trim(), attachments });
    if (sent) {
      setBody('');
      setAttachments([]);
      emitTeamTyping(conversation.id, false);
    }
  };

  const addFiles = async (event) => {
    const files = Array.from(event.target.files || []).slice(0, 10 - attachments.length);
    event.target.value = '';
    if (!files.length) return;
    setUploading(true);
    setUploadError(null);
    try {
      const uploaded = await Promise.all(files.map((file) => onUpload(file)));
      setAttachments((current) => [...current, ...uploaded].slice(0, 10));
    } catch (error) {
      setUploadError(typeof error === 'string' ? error : error?.message || 'Unable to upload this attachment.');
    } finally {
      setUploading(false);
    }
  };

  const conversationSurface = (
    <Paper
      elevation={embedded ? 0 : 12}
      role={embedded ? 'region' : 'dialog'}
      aria-label={conversation ? `Team conversation with ${conversation.name}` : 'Team conversation'}
      sx={embedded ? {
        position: 'relative',
        zIndex: 1,
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        maxWidth: '100%',
        height: '100%',
        minHeight: 0,
        overflow: 'hidden',
        border: 0,
        borderRadius: 0
      } : {
          position: 'fixed', zIndex: theme.zIndex.drawer + 3, display: 'flex', flexDirection: 'column', overflow: 'hidden',
          width: isMobile ? '100%' : 420, maxWidth: '100vw',
          height: isMobile ? `calc(100vh - ${theme.header.height})` : 590,
          maxHeight: isMobile ? undefined : `calc(100vh - ${theme.header.height} - 24px)`,
          right: isMobile ? 0 : (drawerOpen ? DRAWER_WIDTH + 14 : 16), bottom: 0,
          top: isMobile ? theme.header.height : 'auto',
          borderRadius: isMobile ? 0 : `${theme.general.borderRadius} ${theme.general.borderRadius} 0 0`,
          border: `1px solid ${theme.colors.alpha.black[20]}`,
          transition: theme.transitions.create('right', { duration: theme.transitions.duration.shorter })
      }}
    >
        <Box sx={{ px: 1.25, py: 1, display: 'flex', alignItems: 'center', borderBottom: `1px solid ${theme.colors.alpha.black[10]}` }}>
          <Avatar sx={{ width: 34, height: 34, mr: 1, fontSize: 12 }}>
            {conversation?.kind === 'group' ? <GroupsTwoToneIcon fontSize="small" /> : initials(conversation?.name)}
          </Avatar>
          <Box flex={1} minWidth={0}>
            <Typography variant="h5" noWrap>{conversation?.name || 'Team conversation'}</Typography>
            <Typography variant="caption" color="text.secondary" noWrap display="block">
              {conversation?.kind === 'group'
                ? `${conversation.participants?.length || 0} participants`
                : 'Direct team message'}
            </Typography>
          </Box>
          {conversation?.kind === 'group' && viewer?.canManageGroups ? (
            <>
              <Tooltip title="Manage group"><IconButton aria-label="Manage group" size="small" onClick={onManageGroup}><EditTwoToneIcon fontSize="small" /></IconButton></Tooltip>
              <Tooltip title="Archive group"><IconButton aria-label="Archive group" size="small" onClick={onArchiveGroup}><ArchiveTwoToneIcon fontSize="small" /></IconButton></Tooltip>
            </>
          ) : null}
          {!embedded && onOpenFull ? (
            <Tooltip title="Open Team Messenger">
              <IconButton aria-label="Open Team Messenger" size="small" onClick={onOpenFull}>
                <OpenInFullTwoToneIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          ) : null}
          {onClose ? (
            <Tooltip title={embedded && isMobile ? 'Back to team' : 'Close conversation'}>
              <IconButton
                aria-label={embedded && isMobile ? 'Back to team' : 'Close conversation'}
                size="small"
                onClick={onClose}
              >
                {embedded && isMobile
                  ? <ArrowBackTwoToneIcon fontSize="small" />
                  : <CloseTwoToneIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
          ) : null}
        </Box>

        <Box ref={scrollRef} sx={{ flex: 1, minHeight: 0, overflowY: 'auto', bgcolor: theme.colors.alpha.black[5], px: 1.5, py: 1.25 }}>
          {hasMore ? (
            <Box textAlign="center" mb={1}><Button size="small" disabled={loadingMore} onClick={onLoadMore}>{loadingMore ? 'Loading...' : 'Load earlier messages'}</Button></Box>
          ) : null}
          {loading ? <Box py={6} display="flex" justifyContent="center"><CircularProgress size={28} /></Box> : null}
          {error ? <Alert severity="error" action={<Button size="small" onClick={onRetry}>Retry</Button>}>{error}</Alert> : null}
          {!loading && !error && !messages.length ? (
            <Box py={6} textAlign="center"><Typography variant="body2" color="text.secondary">Start the conversation.</Typography></Box>
          ) : null}
          <Stack spacing={1}>
            {messages.map((message) => {
              const own = message.sender?.actorKey === viewer?.actorKey;
              return (
                <Box key={message.id} display="flex" justifyContent={own ? 'flex-end' : 'flex-start'}>
                  <Box sx={{ maxWidth: '82%' }}>
                    {!own ? <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>{message.sender?.displayName}</Typography> : null}
                    <Box sx={{
                      px: 1.25, py: 0.85, borderRadius: 1.5,
                      bgcolor: own ? 'primary.main' : 'background.paper', color: own ? 'primary.contrastText' : 'text.primary',
                      boxShadow: own ? 'none' : theme.shadows[1]
                    }}>
                      {message.body ? <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{message.body}</Typography> : null}
                      {(message.attachments || []).map((attachment) => <Attachment key={attachment.id || attachment.url} attachment={attachment} own={own} />)}
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: own ? 'right' : 'left', mt: 0.25, px: 0.5 }}>
                      {moment(message.createdAt).format('h:mm A')}
                    </Typography>
                  </Box>
                </Box>
              );
            })}
          </Stack>
        </Box>
        {typingNames.length ? <Typography variant="caption" color="text.secondary" sx={{ px: 2, pt: 0.5 }}>{typingNames.join(', ')} typing...</Typography> : null}
        {uploadError ? <Alert severity="error" onClose={() => setUploadError(null)} sx={{ mx: 1, mt: 0.75 }}>{uploadError}</Alert> : null}
        {sendError ? <Alert severity="error" sx={{ mx: 1, mt: 0.75 }}>{sendError}</Alert> : null}
        {attachments.length ? (
          <Stack direction="row" spacing={0.75} sx={{ px: 1.25, pt: 0.75, overflowX: 'auto' }}>
            {attachments.map((attachment, index) => (
              <Button key={attachment.id || index} size="small" variant="outlined" onClick={() => setAttachments((current) => current.filter((_, itemIndex) => itemIndex !== index))}>
                {attachment.filename || `Attachment ${index + 1}`} ×
              </Button>
            ))}
          </Stack>
        ) : null}
        <Divider />
        <Box sx={{ p: 1, display: 'flex', gap: 0.75, alignItems: 'flex-end' }}>
          <input ref={fileRef} hidden multiple type="file" onChange={addFiles} />
          <Tooltip title="Attach files"><span><IconButton disabled={uploading || attachments.length >= 10} onClick={() => fileRef.current?.click()}><AttachFileTwoToneIcon /></IconButton></span></Tooltip>
          <TextField
            fullWidth
            multiline
            maxRows={4}
            size="small"
            value={body}
            onChange={changeBody}
            placeholder="Message your team"
            onKeyDown={(event) => {
              if (event.key === 'Enter' && !event.shiftKey) {
                event.preventDefault();
                submit();
              }
            }}
          />
          <Tooltip title="Send message"><span><IconButton color="primary" disabled={sending || uploading || (!body.trim() && !attachments.length)} onClick={submit}><SendTwoToneIcon /></IconButton></span></Tooltip>
        </Box>
    </Paper>
  );

  if (embedded) return conversationSurface;

  return (
    <Slide direction="up" in={open} mountOnEnter unmountOnExit>
      {conversationSurface}
    </Slide>
  );
}

export default TeamConversation;
